import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
    static targets = ["trigger", "content"]
    static values = {
        textOpened: String,
        textClosed: String,
        collapseArea: String, // Selector for the parent area containing multiple sections
        sectionWrapper: String, // Selector for individual section wrappers within the area
        open: { type: Boolean, default: false } // Default to collapsed
    }

    connect() {
        if (this.openValue) {
            this.performOpenActions(false); // false - do not close others on initial load
        } else {
            this.performCloseActions();
        }
        this.updateTriggerAppearanceOnly();
    }

    openThisSectionAndScrollTo({ detail: { modalTargetSelector, scrollOn } }) {
        if (!this.openValue) {
            this.performOpenActions(true); // true - close other sections in the accordion
        }

        if (modalTargetSelector && this.hasContentTarget) {
            const elementToScrollTo = this.contentTarget.querySelector(modalTargetSelector);
            if (elementToScrollTo) {
                const currentField = elementToScrollTo.closest(".field");
                if (currentField && scrollOn === "true") {
                    setTimeout(() => {
                        currentField.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 400);
                }
            } else {
                console.warn(`CollapseController: Element to scroll to "${modalTargetSelector}" not found within contentTarget of`, this.element);
            }
        }
    }

    toggle(event) {
        if (event) event.preventDefault();
        if (!this.openValue) {
            this.performOpenActions(true); // true - close other sections
        } else {
            this.performCloseActions();
        }
    }

    performOpenActions(closeOthers) {
        this.openValue = true;
        if (this.hasContentTarget) this.contentTarget.classList.add("show");
        this.element.classList.remove("collapsed");
        this.element.classList.add("show");
        this.updateTriggerAppearanceOnly();

        if (closeOthers && this.hasCollapseAreaValue && this.hasSectionWrapperValue) {
            this.closeOtherSections();
        }
    }

    performCloseActions() {
        this.openValue = false;
        if (this.hasContentTarget) this.contentTarget.classList.remove("show");
        this.element.classList.add("collapsed");
        this.element.classList.remove("show");
        this.updateTriggerAppearanceOnly();
    }

    updateTriggerAppearanceOnly() {
        if (!this.hasTriggerTarget) return;

        if (this.openValue) {
            this.triggerTarget.classList.remove("collapsed");
            this.triggerTarget.classList.add("collapse-trigger--opened");
            if (this.hasTextOpenedValue) this.triggerTarget.innerHTML = this.textOpenedValue;
        } else {
            this.triggerTarget.classList.add("collapsed");
            this.triggerTarget.classList.remove("collapse-trigger--opened");
            if (this.hasTextClosedValue) this.triggerTarget.innerHTML = this.textClosedValue;
        }
    }

    closeOtherSections() {
        // Ensure `collapseAreaValue` and `sectionWrapperValue` are present
        if (!this.hasCollapseAreaValue || !this.hasSectionWrapperValue) {
            console.warn("CollapseController: Missing collapseAreaValue or sectionWrapperValue for closing other sections.", this.element);
            return;
        }

        const collapseAreaElement = document.querySelector(this.collapseAreaValue);
        if (!collapseAreaElement) {
            console.warn(`CollapseController: Collapse area element "${this.collapseAreaValue}" not found.`, this.element);
            return;
        }

        // Find all section wrappers within the collapse area that have the 'collapse' controller
        const otherWrapperElements = Array.from(collapseAreaElement.querySelectorAll(`${this.sectionWrapperValue}[data-controller~="collapse"]`));

        otherWrapperElements.forEach(wrapperElement => {
            // Don't try to close itself
            if (wrapperElement === this.element) {
                return;
            }

            // Get the controller instance for this specific wrapper element

            const controllerInstance = this.application.getControllerForElementAndIdentifier(wrapperElement, "collapse");

            if (controllerInstance && controllerInstance.openValue) {
                controllerInstance.performCloseActions(); // Call the method to close it
            }
        });
    }
}
